import { ConnectionError, parseErrorResponse } from "./errors";
import type { PayBridgeConfig } from "./types";

const DEFAULT_BASE_URL = "https://api.paybridgenp.com";
const DEFAULT_TIMEOUT = 30_000;
const DEFAULT_MAX_RETRIES = 2;
const RETRY_STATUSES = new Set([500, 502, 503, 504]);
const INITIAL_BACKOFF_MS = 500;

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function backoff(attempt: number): number {
  return INITIAL_BACKOFF_MS * 2 ** (attempt - 1) + Math.random() * 100;
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly timeout: number;
  private readonly maxRetries: number;

  constructor(config: PayBridgeConfig) {
    this.baseUrl = (config.baseUrl ?? DEFAULT_BASE_URL).replace(/\/$/, "");
    this.apiKey = config.apiKey;
    this.timeout = config.timeout ?? DEFAULT_TIMEOUT;
    this.maxRetries = config.maxRetries ?? DEFAULT_MAX_RETRIES;
  }

  async request<T>(method: string, path: string, body?: unknown, idempotencyKey?: string): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    // Retrying a POST/PATCH/DELETE that may already have been applied is how a
    // network blip becomes two checkout sessions, two subscriptions, two
    // invoices. Every method used to be retried on a connection error and on
    // 500/502/503/504, with no idempotency key on any attempt (external
    // review, 2026-07-28).
    //
    // GET is safe by definition, so only GET auto-retries now.
    //
    // We also send an Idempotency-Key on unsafe requests. Replay protection is
    // applied per route by the API, so the key is protection wherever the
    // server offers it and it makes a caller's own retry safe there; it is
    // NOT a licence for this client to retry writes
    // automatically. Re-enable that only when every mutating route is wrapped.
    const isSafe = method.toUpperCase() === "GET";
    const headers: Record<string, string> = {
      Authorization: `Bearer ${this.apiKey}`,
      "Content-Type": "application/json",
      "User-Agent": "PayBridgeNP-SDK/5.7.0",
    };
    if (!isSafe) {
      headers["Idempotency-Key"] = idempotencyKey ?? crypto.randomUUID();
    }

    let attempt = 0;

    while (true) {
      attempt++;

      let res: Response;
      try {
        res = await fetch(url, {
          method,
          headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
          signal: AbortSignal.timeout(this.timeout),
        });
      } catch (err) {
        if (!isSafe || attempt > this.maxRetries) {
          throw new ConnectionError(`Connection error: ${(err as Error).message}`);
        }
        await sleep(backoff(attempt));
        continue;
      }

      if (res.ok) {
        return res.json() as Promise<T>;
      }

      if (isSafe && RETRY_STATUSES.has(res.status) && attempt <= this.maxRetries) {
        const retryAfter = res.headers.get("Retry-After");
        const delay = retryAfter ? parseInt(retryAfter) * 1000 : backoff(attempt);
        await sleep(delay);
        continue;
      }

      let raw: Record<string, unknown> | null = null;
      try {
        raw = await res.json() as Record<string, unknown>;
      } catch {
        // Body wasn't JSON. Will surface as `HTTP <status>` with no detail.
      }

      throw parseErrorResponse(res.status, raw, res.headers.get("Retry-After"));
    }
  }

  get<T>(path: string) { return this.request<T>("GET", path); }
  post<T>(path: string, body: unknown, idempotencyKey?: string) { return this.request<T>("POST", path, body, idempotencyKey); }
  patch<T>(path: string, body: unknown, idempotencyKey?: string) { return this.request<T>("PATCH", path, body, idempotencyKey); }
  delete<T>(path: string, idempotencyKey?: string) { return this.request<T>("DELETE", path, undefined, idempotencyKey); }
}
